'use client';

import { useRouter } from '@bprogress/next/app';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { VariantProps } from 'tailwind-variants';

import { AUTH_MAX_AGE } from '@/fo/api/magento';
import ArrowRightIcon from '@/fo/icons/base/icons/arrow24.svg';
import { UiAuthInputCode } from '@/fo/ui/auth/input/code';
import { UiKitButtonBack } from '@/fo/ui/kit/button/back';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitInputBase, UiKitInputBaseProps } from '@/fo/ui/kit/input/base';
import { UiKitInputPassword } from '@/fo/ui/kit/input/password';
import { toastBase } from '@/fo/ui/kit/toast/base';
import { UiKitToggleBase } from '@/fo/ui/kit/toggle/base';
import { useForm } from '@/fo/ui/mod/form/base';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';
import { phoneMask } from '@/fo/util/format-phone-number';

import { loginByPhone, sendAuthCode } from '../api/auth';

import { styles } from './FeatureAuthFormLogin.styles';
import { formAction } from './formAction';

type Variants = VariantProps<typeof styles>;
type NativeElement = React.HTMLAttributes<HTMLDivElement>;

export interface FeatureAuthFormLoginProps {
  color?: Variants['color'];
  onClick?: NativeElement['onClick'];
}

export function FeatureAuthFormLogin({
  color,
  ...props
}: FeatureAuthFormLoginProps) {
  const slots = styles({ color });
  const t = useTranslations();
  const router = useRouter();

  const { state, pending, action, getFieldProps } = useForm(formAction);
  const [switchContact, setSwitchContact] = useState<'phone' | 'email'>(
    'email'
  );
  const [formStep, setFormStep] = useState<'login' | 'authCode'>('login');
  const [loginValue, setLoginValue] = useState('');
  const loginInputRef = useRef<HTMLInputElement>(null);
  const switchContactProps = getFieldProps(switchContact);

  const beforeMaskedPhoneInputChange: UiKitInputBaseProps['beforeMaskedStateChange'] =
    (state) => {
      if (!state?.previousState?.value && state?.nextState?.value === '8') {
        return { ...state.nextState, value: '+7 (' };
      }

      if (
        state?.nextState?.value === '+7 (' &&
        state?.previousState?.value === '+7 ('
      ) {
        return { ...state.nextState, value: '' };
      }

      if (switchContact === 'email') {
        return { ...state.nextState, value: state.nextState.value || '' };
      }

      if (
        state?.nextState?.value &&
        !/^[0-9 ()+-]+$/.test(state.nextState.value)
      ) {
        flushSync(() => setSwitchContact('email'));
        return { ...state.nextState, value: state.currentState?.value || '' };
      }

      return { ...state.nextState };
    };

  const onSwitchContactChange = (value: string) => {
    if (value === '8') {
      value = '+7 (';
      setSwitchContact('phone');
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailFormat = emailRegex.test(value);

    const startsWithPlus7 = value.startsWith('+7 (');
    const minPhoneLength = 6;
    const isPhoneChars = /^[\d ()+-]+$/.test(value);

    if (isEmailFormat) {
      setSwitchContact('email');
    } else if (
      startsWithPlus7 ||
      (value.length >= minPhoneLength && isPhoneChars)
    ) {
      setSwitchContact('phone');
    } else {
      setSwitchContact('email');
    }

    setLoginValue(value);
    switchContactProps.onChange?.(value);
  };

  const onSuccess = () => {
    const targetUrl = utilEventAuthModal.getRedirectUrl();

    if (targetUrl) {
      utilEventAuthModal.close();
      window.location.assign(targetUrl);
      return;
    }

    utilEventAuthModal.close();
    router.refresh();
  };

  useEffect(() => {
    if (state.status === 'success' && !pending) {
      onSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, pending]);

  useEffect(() => {
    loginInputRef.current?.focus();
  }, [switchContact]);

  return (
    <div
      className={slots.base()}
      {...props}
    >
      {formStep === 'login' && (
        <form action={action}>
          <div className={slots.fields()}>
            <div>
              <UiKitInputBase
                {...switchContactProps}
                mask={switchContact === 'phone' ? phoneMask : ''}
                onChange={onSwitchContactChange}
                ref={loginInputRef}
                beforeMaskedStateChange={beforeMaskedPhoneInputChange}
                type={switchContact === 'phone' ? 'tel' : 'text'}
                label={t('form.label.emailOrPhone')}
                value={loginValue}
                disabled={pending}
              />
              {switchContact === 'phone' &&
                loginValue.length === phoneMask.length && (
                  <UiKitButtonBase
                    color="ghost"
                    className={slots.loginBySMS()}
                    onClick={async () => {
                      setFormStep('authCode');

                      const response = await sendAuthCode(loginValue);

                      if (response.error) {
                        toastBase(response.error, { status: 'error' });
                      }
                    }}
                  >
                    <span>{t('form.button.loginBySMS')}</span>
                    <ArrowRightIcon className={slots.arrowIcon()} />
                  </UiKitButtonBase>
                )}
            </div>
            <UiKitInputPassword
              label={t('form.label.password')}
              {...getFieldProps('password')}
              disabled={pending}
            />
            <div className={slots.row()}>
              <UiKitToggleBase
                label={t('form.label.rememberMe')}
                {...getFieldProps('rememberMe')}
                disabled={pending}
              />
              <UiKitButtonBase
                color="ghost"
                className={slots.forgotPassword()}
                onClick={() => utilEventAuthModal.showForgotPassword()}
              >
                <span>{t('form.button.forgotPassword')}</span>
              </UiKitButtonBase>
            </div>
          </div>

          <UiKitButtonBase
            type="submit"
            className={slots.button()}
            loading={pending}
          >
            <span>{t('form.button.login')}</span>
          </UiKitButtonBase>
        </form>
      )}
      {formStep === 'authCode' && (
        <>
          <div>
            <UiKitButtonBack onClick={() => setFormStep('login')} />
          </div>
          <UiAuthInputCode
            phoneNumber={loginValue}
            onSendCode={async (code) => {
              const response = await loginByPhone(
                loginValue,
                code,
                state.data?.rememberMe ? { maxAge: AUTH_MAX_AGE } : undefined
              );

              if (response.status === 'success') {
                setFormStep('login');
                onSuccess();
              }

              if (response.status === 'error' && response.toastMessage) {
                toastBase(response.toastMessage, { status: 'error' });
              }

              return response;
            }}
            onResend={async () => {
              const response = await sendAuthCode(loginValue);

              if (response.error) {
                toastBase(response.error, { status: 'error' });
              }
            }}
          />
        </>
      )}
    </div>
  );
}
